import { Component } from "@angular/core";
import {
  VAIconRegistryService,
  chevronRight,
  slidersHorizontal,
  chevronDown,
} from "@va/core-library/icons";
import { Subscription } from "rxjs";
import { ComparativeAnalysisService } from "src/app/comparative-analysis/comparative-analysis.service";
import { ActivatedRoute, Router, NavigationEnd, Scroll } from "@angular/router";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
} from "rxjs/operators";
import { UntypedFormControl } from "@angular/forms";
import { getMarketGeoDataFromUrl } from "src/app/comparative-analysis/comparative-analysis-utils";

@Component({
  selector: "app-comparative-search-menu",
  templateUrl: "./comparative-search-menu.component.html",
  styleUrls: ["./comparative-search-menu.component.scss"],
})
export class ComparativeSearchMenuComponent {
  currenttabType;
  searchedTerm;
  isSearchMenuVisible = false;
  activatedOptions = "industry";
  menuList = [
    {
      id: "industry",
      label: "Industry Peers",
      dataProperty: "industryMenuData",
    },
    {
      id: "comp_table",
      label: "Comp Tables",
      isLeaf: false,
      dataProperty: "compTableMenuData",
    },
    {
      id: "custom_analysis",
      label: "Custom Analysis",
      isLeaf: false,
      dataProperty: "customAnalysisMenuData",
    },
    {
      id: "watchlist",
      label: "Watchlists",
      isLeaf: false,
      dataProperty: "watchlistMenuData",
    },
  ];
  menuData = {
    industryMenuData: [],
    compTableMenuData: [],
    customAnalysisMenuData: [],
    watchlistMenuData: [],
  };
  marketCapData;
  selectedMarketCapsId;
  selectedCountriesId;
  selectedRegionsId;
  geographyData;
  otherGeoLen: number;
  selectionInMegaMenu;
  selectedIndustryData;
  selectedSystemData;
  selectedWatchlistId;
  selectedCustomAnalysisId;

  //loaders
  isIndustryMenuApiInProgress = false;
  isCustomMenuApiInProgress = false;
  isCompMenuApiInProgress = false;
  isWatchlistMenuApiInProgress = false;
  isMarketGeoApiInProgress = false;

  // subscribers
  comparativEanalysisMenuSubscriber: Subscription;
  marketGeoSubscriber: Subscription;
  routeParamSubscriber: Subscription;
  comparativeMenuTreeIdDetailsSubscriber: Subscription;

  queryField: UntypedFormControl = new UntypedFormControl();

  constructor(
    private iconRegistry: VAIconRegistryService,
    private comparativeAnalysisService: ComparativeAnalysisService,
    public router: Router,
    private route: ActivatedRoute,
    private dataService: ComparativeAnalysisService,
  ) {
    this.iconRegistry.registerIcons([
      chevronRight,
      slidersHorizontal,
      chevronDown,
    ]);
  }

  ngOnInit() {
    this.routeParamSubscriber = this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof Scroll &&
            event.routerEvent instanceof NavigationEnd,
        ),
        map((event) =>
          event instanceof Scroll
            ? (event.routerEvent as NavigationEnd)
            : (event as NavigationEnd),
        ),
      )
      .subscribe(() => {
        if (this.route.snapshot.queryParams) {
          this.selectedIndustryData = undefined;
          this.selectedSystemData = undefined;
          this.selectedWatchlistId = undefined;
          this.selectedCustomAnalysisId = undefined;
          const queryParams = this.route.snapshot.queryParams;
          if (queryParams.comp_type === "industry") {
            this.activatedOptions = "industry";
          } else if (queryParams.comp_type === "system") {
            this.activatedOptions = "comp_table";
          } else if (queryParams.comp_type === "watchlist") {
            this.activatedOptions = "watchlist";
            this.selectedWatchlistId = queryParams.id
              ? parseInt(queryParams.id)
              : queryParams.id;
          } else if (window.location.pathname.split("/").includes("user")) {
            this.activatedOptions = "custom_analysis";
            const id = window.location.pathname.split("/")[3];
            this.selectedCustomAnalysisId = id ? parseInt(id) : id;
            this.menuData.customAnalysisMenuData = this.menuData
              .customAnalysisMenuData
              ? this.menuData.customAnalysisMenuData.map((item) => ({
                  ...item,
                  selected: item.id === this.selectedCustomAnalysisId,
                }))
              : this.menuData.customAnalysisMenuData;
          }

          // get parent id for industy and system table
          if (
            queryParams.comp_type === "industry" ||
            queryParams.comp_type === "system"
          ) {
            this.comparativeMenuTreeIdDetailsSubscriber = this.dataService
              .getComparativeMenuTreeIdDetails(
                queryParams.comp_type,
                queryParams.id,
              )
              .subscribe((response: any) => {
                if ((response.status === "success", response.data)) {
                  if (queryParams.comp_type === "industry") {
                    this.activatedOptions = "industry";
                    this.selectedIndustryData = {
                      industry_id: parseInt(queryParams.id),
                      subgroup_id: queryParams.sub_id
                        ? parseInt(queryParams.sub_id)
                        : undefined,
                      parent_id: response.data.level_one_id,
                    };
                  } else if (queryParams.comp_type === "system") {
                    this.activatedOptions = "comp_table";
                    this.selectedSystemData = {
                      level_one_id: response.data.level_one_id,
                      level_two_id: response.data.level_two_id,
                      id: parseInt(queryParams.id),
                    };
                  }
                }
              });
          }

          // fetch market geo data
          const marketGeoPayload = getMarketGeoDataFromUrl(queryParams);
          this.selectedMarketCapsId = marketGeoPayload.market_cap;
          this.selectedRegionsId = marketGeoPayload.region_id;
          this.selectedCountriesId = marketGeoPayload.country_id;
          this.marketGeoSubscriber = this.comparativeAnalysisService
            .fetchMarketAndGeoData(marketGeoPayload)
            .subscribe((response: any) => {
              if (response.status === "success") {
                this.marketCapData = response.data.market_cap;
                this.geographyData = response.data.geographies;
              }
              this.isMarketGeoApiInProgress = false;
              this.otherGeoLen = this.geographyData.length - 2;
            });
        }
      });

    this.isIndustryMenuApiInProgress = true;
    this.isCustomMenuApiInProgress = true;
    this.isCompMenuApiInProgress = true;
    this.isWatchlistMenuApiInProgress = true;
    this.isMarketGeoApiInProgress = true;
    // fetch menu data
    this.fetchMenuData();
  }

  ngOnDestroy() {
    if (this.routeParamSubscriber) {
      this.routeParamSubscriber.unsubscribe();
    }
    if (this.comparativeMenuTreeIdDetailsSubscriber) {
      this.comparativeMenuTreeIdDetailsSubscriber.unsubscribe();
    }
  }

  ngAfterViewInit() {
    this.queryField.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((text) => {
        this.searchedTerm = text;
      });
  }

  fetchMenuData(): void {
    this.comparativEanalysisMenuSubscriber = this.comparativeAnalysisService
      .fetchComparativeAnalysisMenuData("industry")
      .subscribe((response) => {
        if (
          response.status === "success" &&
          response.data &&
          response.data.industry
        ) {
          this.menuData.industryMenuData = [...response.data.industry];
        }
        this.isIndustryMenuApiInProgress = false;
      });

    this.comparativEanalysisMenuSubscriber = this.comparativeAnalysisService
      .fetchComparativeAnalysisMenuData("custom_analysis")
      .subscribe((response) => {
        if (
          response.status === "success" &&
          response.data &&
          response.data.custom_analysis
        ) {
          this.menuData.customAnalysisMenuData =
            response.data.custom_analysis.map((item) => ({
              ...item,
              selected: item.id === this.selectedCustomAnalysisId,
            }));
        }
        this.isCustomMenuApiInProgress = false;
      });

    this.comparativEanalysisMenuSubscriber = this.comparativeAnalysisService
      .fetchComparativeAnalysisMenuData("comp_table")
      .subscribe((response) => {
        if (
          response.status === "success" &&
          response.data &&
          response.data.comp_table
        ) {
          this.menuData.compTableMenuData = [...response.data.comp_table];
        }
        this.isCompMenuApiInProgress = false;
      });

    this.comparativEanalysisMenuSubscriber = this.comparativeAnalysisService
      .fetchComparativeAnalysisMenuData("watchlist")
      .subscribe((response) => {
        if (
          response.status === "success" &&
          response.data &&
          response.data.watchlist
        ) {
          this.menuData.watchlistMenuData = response.data.watchlist;
        }
        this.isWatchlistMenuApiInProgress = false;
      });
  }

  handleVisibleChange(value) {
    if (!value) {
      this.queryField.reset();
    }
  }

  handleIndustryMenuCancelButtonClick() {
    this.closePopover();
  }

  handleWatchlistMenuCancelButtonClick() {
    this.closePopover();
  }

  closePopover() {
    this.isSearchMenuVisible = false;
  }

  handleIndustryMenuApplyButtonClick(data) {
    let urlToNavigate = "/comparative-analysis/current";
    urlToNavigate += `?comp_type=industry`;
    if (data.industry_id) {
      urlToNavigate += `&id=${data.industry_id}`;
    }
    if (data.subindustry_id) {
      urlToNavigate += `&sub_id=${data.subindustry_id}`;
    }
    // if (data.sector_id) {
    //   urlToNavigate += `&parent_id=${data.sector_id}`;
    // }
    if (data.market_cap && data.market_cap.length) {
      urlToNavigate += `&market_cap=${data.market_cap.join("&market_cap=")}`;
    }
    if (data.region_id && data.region_id.length) {
      urlToNavigate += `&region_id=${data.region_id.join("&region_id=")}`;
    }
    if (data.country_id && data.country_id.length > 0) {
      urlToNavigate += `&country_id=${data.country_id.join("&country_id=")}`;
    }
    this.router.navigateByUrl(urlToNavigate);
    this.closePopover();
  }

  handleWatchlistMenuApplyButtonClick(data) {
    let urlToNavigate = "/comparative-analysis/current";
    urlToNavigate += `?comp_type=watchlist`;
    if (data.watchlist_id) {
      urlToNavigate += `&id=${data.watchlist_id}`;
    }
    if (data.watchlistName) {
      data.watchlistName = data.watchlistName.replaceAll("&", "%26");
      urlToNavigate += `&comps_name=${data.watchlistName}`;
    }
    if (data.market_cap && data.market_cap.length) {
      urlToNavigate += `&market_cap=${data.market_cap.join("&market_cap=")}`;
    }
    if (data.region_id && data.region_id.length) {
      urlToNavigate += `&region_id=${data.region_id.join("&region_id=")}`;
    }
    if (data.country_id && data.country_id.length > 0) {
      urlToNavigate += `&country_id=${data.country_id.join("&country_id=")}`;
    }
    this.router.navigateByUrl(urlToNavigate);
    this.closePopover();
  }

  handlSystemItemClick(data) {
    let urlToNavigate = "/comparative-analysis/current";
    urlToNavigate += `?comp_type=system`;
    if (data) {
      if (data.id) {
        urlToNavigate += `&id=${data.id}`;
      }
      // if (data.levelOneId) {
      //   urlToNavigate += `&lvl1=${data.levelOneId}`;
      // }
      // if (data.levelTwoId) {
      //   urlToNavigate += `&lvl2=${data.levelTwoId}`;
      // }
      this.router.navigateByUrl(urlToNavigate);
    }
    this.closePopover();
  }

  handleCustomItemClick(data) {
    let urlToNavigate = "/comparative-analysis/user";
    if (data.id) {
      urlToNavigate += `/${data.id}`;
      this.router.navigateByUrl(urlToNavigate).then(() => {
        this.dataService.updateLeftPanel(false);
      });
    }
    this.closePopover();
  }
}
